/**
 * Statistics Filter Utilities
 * Provides consistent date/timezone handling and filter state management
 */

/**
 * Get current date in UTC-5 (business timezone)
 * @returns {Date} Current date adjusted to UTC-5
 */
export function getCurrentDateUTC5() {
  const now = new Date();
  // UTC-5 offset: 5 hours = 5 * 60 * 60 * 1000 milliseconds
  const utc5OffsetMs = 5 * 60 * 60 * 1000;
  return new Date(now.getTime() - utc5OffsetMs);
}

/**
 * Get start and end dates for a period in UTC-5
 * @param {string} period - 'day' | 'week' | 'month' | 'custom'
 * @param {string|null} startDate - ISO string for custom period start
 * @param {string|null} endDate - ISO string for custom period end
 * @returns {{startDate: string, endDate: string, period: string}} ISO date strings
 */
export function getDateRangeForPeriod(period, startDate = null, endDate = null) {
  const nowUTC5 = getCurrentDateUTC5();
  let start, end;

  if (period === 'custom' && startDate && endDate) {
    // For custom, use provided dates but ensure they're in UTC-5 context
    start = new Date(startDate);
    end = new Date(endDate);
    // Set to start/end of day in UTC-5
    start.setUTCHours(5, 0, 0, 0); // 5 AM UTC = 0 AM UTC-5
    end.setUTCHours(29, 59, 59, 999); // 29:59:59 UTC = 23:59:59 UTC-5
  } else if (period === 'day') {
    // Today in UTC-5
    start = new Date(nowUTC5);
    start.setUTCHours(0, 0, 0, 0);
    end = new Date(nowUTC5);
    end.setUTCHours(23, 59, 59, 999);
  } else if (period === 'week') {
    // This week (Sunday to Saturday) in UTC-5
    const dayOfWeek = nowUTC5.getUTCDay();
    start = new Date(nowUTC5);
    start.setUTCDate(nowUTC5.getUTCDate() - dayOfWeek);
    start.setUTCHours(0, 0, 0, 0);
    end = new Date(start);
    end.setUTCDate(start.getUTCDate() + 6);
    end.setUTCHours(23, 59, 59, 999);
  } else if (period === 'month') {
    start = new Date(Date.UTC(nowUTC5.getUTCFullYear(), nowUTC5.getUTCMonth(), 1, 5, 0, 0, 0));
    end = new Date(Date.UTC(nowUTC5.getUTCFullYear(), nowUTC5.getUTCMonth() + 1, 0, 29, 59, 59, 999));
  } else if (period === 'quarter') {
    const month = nowUTC5.getUTCMonth();
    const q = Math.floor(month / 3);
    const quarterStartMonth = q * 3;
    const quarterEndMonth = (q + 1) * 3;
    start = new Date(Date.UTC(nowUTC5.getUTCFullYear(), quarterStartMonth, 1, 5, 0, 0, 0));
    end = new Date(Date.UTC(nowUTC5.getUTCFullYear(), quarterEndMonth, 0, 29, 59, 59, 999));
  } else if (period === 'year') {
    start = new Date(Date.UTC(nowUTC5.getUTCFullYear(), 0, 1, 5, 0, 0, 0));
    end = new Date(Date.UTC(nowUTC5.getUTCFullYear(), 11, 31, 29, 59, 59, 999));
  } else {
    start = new Date(Date.UTC(nowUTC5.getUTCFullYear(), nowUTC5.getUTCMonth(), 1, 5, 0, 0, 0));
    end = new Date(Date.UTC(nowUTC5.getUTCFullYear(), nowUTC5.getUTCMonth() + 1, 0, 29, 59, 59, 999));
  }

  return {
    startDate: start.toISOString(),
    endDate: end.toISOString(),
    period
  };
}

export function getLast5MonthsRangeUTC5() {
  const nowUTC5 = getCurrentDateUTC5();
  const end = new Date(Date.UTC(nowUTC5.getUTCFullYear(), nowUTC5.getUTCMonth() + 1, 0, 29, 59, 59, 999));
  const start = new Date(Date.UTC(nowUTC5.getUTCFullYear(), nowUTC5.getUTCMonth() - 4, 1, 5, 0, 0, 0));
  return {
    startDate: start.toISOString(),
    endDate: end.toISOString()
  };
}

export function getPeriodPresetFromDateRange(dateRangeKey) {
  const map = {
    today: 'day',
    this_week: 'week',
    this_month: 'month',
    this_quarter: 'quarter',
    this_year: 'year',
    custom: 'custom'
  };
  return map[dateRangeKey] || 'month';
}

export function getStatisticsResolvedDateRange(filterValues, selectedDateRangeKey) {
  const periodPreset = getPeriodPresetFromDateRange(selectedDateRangeKey);
  const customStart =
    filterValues?.dateFrom != null && String(filterValues.dateFrom).trim() !== ''
      ? `${String(filterValues.dateFrom).split('T')[0]}T00:00:00.000Z`
      : null;
  const customEnd =
    filterValues?.dateTo != null && String(filterValues.dateTo).trim() !== ''
      ? `${String(filterValues.dateTo).split('T')[0]}T23:59:59.999Z`
      : null;
  const effectivePeriod =
    selectedDateRangeKey === 'custom' && customStart && customEnd
      ? 'custom'
      : periodPreset;
  const range = getDateRangeForPeriod(
    effectivePeriod,
    effectivePeriod === 'custom' ? customStart : null,
    effectivePeriod === 'custom' ? customEnd : null,
  );
  const fmt = (iso) => {
    try {
      return new Date(iso).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return '';
    }
  };
  return {
    from: fmt(range.startDate),
    to: fmt(range.endDate),
    startIso: range.startDate,
    endIso: range.endDate,
  };
}

export function getLast5MonthsKeysUTC5() {
  const nowUTC5 = getCurrentDateUTC5();
  const keys = [];
  for (let i = 4; i >= 0; i--) {
    const monthDate = new Date(Date.UTC(nowUTC5.getUTCFullYear(), nowUTC5.getUTCMonth() - i, 1));
    const year = monthDate.getUTCFullYear();
    const month = monthDate.getUTCMonth() + 1;
    keys.push(`${year}-${String(month).padStart(2, '0')}`);
  }
  return keys;
}

export function getBarPeriod(preset, from, to) {
  if (preset === 'day' || preset === 'today') {
    return 'day';
  }
  if (preset === 'week' || preset === 'this_week') {
    return 'day';
  }
  if (preset === 'month' || preset === 'this_month') {
    return 'day';
  }
  if (preset === 'quarter' || preset === 'this_quarter') {
    return 'month';
  }
  if (preset === 'year' || preset === 'this_year') {
    return 'month';
  }
  if (preset === 'custom' && from && to) {
    const start = new Date(from);
    const end = new Date(to);
    const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 2) {
      return 'day';
    }
    if (diffDays <= 14) {
      return 'day';
    }
    if (diffDays <= 62) {
      return 'day';
    }
    return 'month';
  }
  return 'month';
}

export function getBarRequestRange(preset, from, to) {
  const dateRange = getDateRangeForPeriod(preset, from, to);
  return {
    startDate: dateRange.startDate,
    endDate: dateRange.endDate
  };
}

const MAX_BAR_POINTS = 7;

export function getBarChartRange(preset, from, to) {
  const nowUTC5 = getCurrentDateUTC5();
  let start;
  let end;

  if (preset === 'day' || preset === 'today') {
    end = new Date(nowUTC5);
    end.setUTCHours(23, 59, 59, 999);
    start = new Date(end);
    start.setUTCDate(start.getUTCDate() - 6);
    start.setUTCHours(0, 0, 0, 0);
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }
  if (preset === 'week' || preset === 'this_week') {
    const dayOfWeek = nowUTC5.getUTCDay();
    start = new Date(nowUTC5);
    start.setUTCDate(nowUTC5.getUTCDate() - dayOfWeek);
    start.setUTCHours(0, 0, 0, 0);
    end = new Date(start);
    end.setUTCDate(start.getUTCDate() + 6);
    end.setUTCHours(23, 59, 59, 999);
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }
  if (preset === 'month' || preset === 'this_month') {
    const y = nowUTC5.getUTCFullYear();
    const m = nowUTC5.getUTCMonth();
    start = new Date(Date.UTC(y, m - 4, 1, 5, 0, 0, 0));
    end = new Date(Date.UTC(y, m + 1, 0, 23, 59, 59, 999));
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }
  if (preset === 'quarter' || preset === 'this_quarter') {
    const q = Math.floor(nowUTC5.getUTCMonth() / 3);
    start = new Date(Date.UTC(nowUTC5.getUTCFullYear(), (q - 1) * 3, 1, 5, 0, 0, 0));
    end = new Date(Date.UTC(nowUTC5.getUTCFullYear(), (q + 2) * 3 - 1, 31, 29, 59, 59, 999));
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }
  if (preset === 'year' || preset === 'this_year') {
    start = new Date(Date.UTC(nowUTC5.getUTCFullYear(), 0, 1, 5, 0, 0, 0));
    end = new Date(Date.UTC(nowUTC5.getUTCFullYear(), 11, 31, 29, 59, 59, 999));
    return { startDate: start.toISOString(), endDate: end.toISOString() };
  }
  if (preset === 'custom' && from && to) {
    const startD = new Date(from);
    const endD = new Date(to);
    const diffDays = Math.ceil((endD - startD) / (1000 * 60 * 60 * 24));
    if (diffDays <= 14) {
      return { startDate: startD.toISOString(), endDate: endD.toISOString() };
    }
    if (diffDays <= 90) {
      const newEnd = new Date(endD);
      const newStart = new Date(newEnd);
      newStart.setUTCDate(newEnd.getUTCDate() - Math.min(diffDays, MAX_BAR_POINTS));
      newStart.setUTCHours(0, 0, 0, 0);
      return { startDate: newStart.toISOString(), endDate: newEnd.toISOString() };
    }
    const newEnd = new Date(endD);
    const newStart = new Date(newEnd);
    newStart.setUTCMonth(newEnd.getUTCMonth() - (MAX_BAR_POINTS - 1));
    newStart.setUTCDate(1);
    newStart.setUTCHours(0, 0, 0, 0);
    return { startDate: newStart.toISOString(), endDate: newEnd.toISOString() };
  }
  start = new Date(Date.UTC(nowUTC5.getUTCFullYear(), nowUTC5.getUTCMonth() - 4, 1, 5, 0, 0, 0));
  end = new Date(Date.UTC(nowUTC5.getUTCFullYear(), nowUTC5.getUTCMonth() + 1, 0, 29, 59, 59, 999));
  return { startDate: start.toISOString(), endDate: end.toISOString() };
}

export function getBarChartGrain(preset, from, to) {
  if (preset === 'day' || preset === 'today') return 'day';
  if (preset === 'week' || preset === 'this_week') return 'day';
  if (preset === 'month' || preset === 'this_month') return 'month';
  if (preset === 'quarter' || preset === 'this_quarter') return 'month';
  if (preset === 'year' || preset === 'this_year') return 'month';
  if (preset === 'custom' && from && to) {
    const start = new Date(from);
    const end = new Date(to);
    const diffDays = Math.ceil((end - start) / (1000 * 60 * 60 * 24));
    if (diffDays <= 14) return 'day';
    if (diffDays <= 90) return 'day';
    return 'month';
  }
  return 'month';
}

export function getBarDisplayPeriod(preset, from, to) {
  return getBarChartGrain(preset, from, to);
}

export function generateExpectedKeys(barPeriod, displayPeriod, from, to) {
  const start = new Date(from);
  const end = new Date(to);
  const keys = [];
  
  if (displayPeriod === 'day') {
    const current = new Date(start);
    current.setUTCHours(0, 0, 0, 0);
    const endDate = new Date(end);
    endDate.setUTCHours(23, 59, 59, 999);
    const days = Math.min(
      Math.ceil((endDate - current) / (1000 * 60 * 60 * 24)) + 1,
      MAX_BAR_POINTS
    );
    for (let i = 0; i < days; i++) {
      const dayDate = new Date(current);
      dayDate.setUTCDate(current.getUTCDate() + i);
      if (dayDate > endDate) break;
      const year = dayDate.getUTCFullYear();
      const month = String(dayDate.getUTCMonth() + 1).padStart(2, '0');
      const day = String(dayDate.getUTCDate()).padStart(2, '0');
      keys.push(`${year}-${month}-${day}`);
    }
  } else if (displayPeriod === 'week') {
    const current = new Date(start);
    current.setUTCHours(0, 0, 0, 0);
    const dayOfWeek = current.getUTCDay();
    const weekStart = new Date(current);
    weekStart.setUTCDate(current.getUTCDate() - dayOfWeek);
    const endDate = new Date(end);
    endDate.setUTCHours(23, 59, 59, 999);
    let weekCount = 0;
    let currentWeek = new Date(weekStart);
    while (currentWeek <= endDate && weekCount < 6) {
      const year = currentWeek.getUTCFullYear();
      const month = String(currentWeek.getUTCMonth() + 1).padStart(2, '0');
      const day = String(currentWeek.getUTCDate()).padStart(2, '0');
      keys.push(`${year}-${month}-${day}`);
      currentWeek.setUTCDate(currentWeek.getUTCDate() + 7);
      weekCount++;
    }
  } else if (displayPeriod === 'month') {
    const startMonth = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
    const endMonth = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
    const monthsDiff = (endMonth.getUTCFullYear() - startMonth.getUTCFullYear()) * 12 + (endMonth.getUTCMonth() - startMonth.getUTCMonth());
    const months = Math.min(Math.max(1, monthsDiff + 1), MAX_BAR_POINTS);
    const now = new Date();
    const maxMonth = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1));
    for (let i = 0; i < months; i++) {
      const monthDate = new Date(Date.UTC(startMonth.getUTCFullYear(), startMonth.getUTCMonth() + i, 1));
      if (monthDate > maxMonth) break;
      const year = monthDate.getUTCFullYear();
      const month = String(monthDate.getUTCMonth() + 1).padStart(2, '0');
      keys.push(`${year}-${month}`);
    }
  }
  
  return keys;
}

export function groupDaysIntoWeeks(dayPoints) {
  const weekMap = new Map();
  
  dayPoints.forEach((point) => {
    if (!point.periodKey) return;
    const date = new Date(point.periodKey + 'T00:00:00');
    const dayOfWeek = date.getUTCDay();
    const weekStart = new Date(date);
    weekStart.setUTCDate(date.getUTCDate() - dayOfWeek);
    const weekKey = `${weekStart.getUTCFullYear()}-${String(weekStart.getUTCMonth() + 1).padStart(2, '0')}-${String(weekStart.getUTCDate()).padStart(2, '0')}`;
    
    if (!weekMap.has(weekKey)) {
      weekMap.set(weekKey, {
        periodKey: weekKey,
        total: 0,
        delivered: 0
      });
    }
    const weekData = weekMap.get(weekKey);
    weekData.total += point.total || 0;
    weekData.delivered += point.delivered || 0;
  });
  
  return Array.from(weekMap.values());
}

export function formatBarLabel(periodKey, displayPeriod) {
  if (!periodKey) return '';
  const str = String(periodKey);

  if (displayPeriod === 'day') {
    if (str.length >= 10) {
      const date = new Date(str.slice(0, 10) + 'T00:00:00');
      if (!isNaN(date.getTime())) {
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    }
    return str;
  }

  if (displayPeriod === 'week') {
    const weekMatch = str.match(/^(\d{4})-W(\d{2})$/);
    if (weekMatch) {
      const [, yearStr, weekStr] = weekMatch;
      const jan4 = new Date(Date.UTC(parseInt(yearStr, 10), 0, 4));
      const dayOfWeek = jan4.getUTCDay() || 7;
      const monday = new Date(jan4);
      monday.setUTCDate(jan4.getUTCDate() - dayOfWeek + 1);
      const weekStart = new Date(monday);
      weekStart.setUTCDate(monday.getUTCDate() + (parseInt(weekStr, 10) - 1) * 7);
      const weekEnd = new Date(weekStart);
      weekEnd.setUTCDate(weekStart.getUTCDate() + 6);
      return `W${weekStr} ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
    }
    if (str.length >= 10) {
      const date = new Date(str.slice(0, 10) + 'T00:00:00');
      if (!isNaN(date.getTime())) {
        const weekStart = new Date(date);
        weekStart.setUTCDate(date.getUTCDate() - date.getUTCDay());
        const weekEnd = new Date(weekStart);
        weekEnd.setUTCDate(weekStart.getUTCDate() + 6);
        return `${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${weekEnd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
      }
    }
    return str;
  }

  if (displayPeriod === 'month') {
    if (str.length >= 7) {
      const parts = str.split('-');
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      if (!isNaN(year) && !isNaN(month)) {
        const date = new Date(year, month - 1, 1);
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      }
    }
    return str;
  }

  return str;
}

/**
 * Default filter values
 */
export const defaultFilters = {
  period: 'month',
  startDate: null,
  endDate: null,
  scope: 'global',
  scopeId: null,
  loadStatus: [],
  paymentType: null,
  paymentStatus: [],
  search: '',
  userRole: [],
  userStatus: [],
  carrierStatus: [],
  customerStatus: [],
  customerType: null,
  shipmentType: null
};

/**
 * Filter schema by tab - defines which filters are visible/active per tab
 */
export const filtersSchemaByTab = {
  overview: {
    period: true,
    scope: true,
    scopeId: true,
    loadStatus: false,
    paymentType: false,
    paymentStatus: false,
    search: false,
    userRole: false,
    userStatus: false,
    carrierStatus: false,
    customerStatus: false,
    customerType: false,
    shipmentType: false
  },
  loads: {
    period: true,
    scope: true,
    scopeId: true,
    loadStatus: true,
    paymentType: false,
    paymentStatus: false,
    search: true,
    userRole: false,
    userStatus: false,
    carrierStatus: false,
    customerStatus: false,
    customerType: false,
    shipmentType: true
  },
  payments: {
    period: true,
    scope: true,
    scopeId: true,
    loadStatus: false,
    paymentType: true,
    paymentStatus: true,
    search: true,
    userRole: false,
    userStatus: false,
    carrierStatus: false,
    customerStatus: false,
    customerType: false,
    shipmentType: false
  },
  users: {
    period: true,
    scope: true,
    scopeId: true,
    loadStatus: false,
    paymentType: false,
    paymentStatus: false,
    search: true
  },
  carriers: {
    period: true,
    scope: true,
    scopeId: true,
    loadStatus: false,
    paymentType: false,
    paymentStatus: false,
    search: true,
    userRole: false,
    userStatus: false,
    carrierStatus: true,
    customerStatus: false,
    customerType: false,
    shipmentType: false
  },
  customers: {
    period: true,
    scope: true,
    scopeId: true,
    loadStatus: false,
    paymentType: false,
    paymentStatus: false,
    search: true,
    userRole: false,
    userStatus: false,
    carrierStatus: false,
    customerStatus: true,
    customerType: true,
    shipmentType: false
  },
  receivable: {
    period: true,
    scope: true,
    scopeId: true,
    loadStatus: false,
    paymentType: false,
    paymentStatus: true,
    search: true,
    userRole: false,
    userStatus: false,
    carrierStatus: false,
    customerStatus: false,
    customerType: false,
    shipmentType: false
  },
  payable: {
    period: true,
    scope: true,
    scopeId: true,
    loadStatus: false,
    paymentType: false,
    paymentStatus: true,
    search: true,
    userRole: false,
    userStatus: false,
    carrierStatus: false,
    customerStatus: false,
    customerType: false,
    shipmentType: false
  }
};

/**
 * Build API query params from applied filters
 * @param {Object} appliedFilters - Applied filter values
 * @param {string} activeTab - Current active tab
 * @returns {Object} API query parameters
 */
export function buildApiParams(appliedFilters, activeTab) {
  const schema = filtersSchemaByTab[activeTab] || filtersSchemaByTab.overview;
  
  // Get date range
  const { startDate, endDate, period } = getDateRangeForPeriod(
    appliedFilters.period || defaultFilters.period,
    appliedFilters.startDate,
    appliedFilters.endDate
  );

  // Build base params
  const params = {
    period,
    startDate,
    endDate
  };

  // Add scope filters (if enabled in schema)
  if (schema.scope && appliedFilters.scope) {
    params.scope = appliedFilters.scope;
    if (appliedFilters.scope !== 'global' && appliedFilters.scopeId) {
      params.scopeId = appliedFilters.scopeId;
    }
  }

  // Add tab-specific filters (only if enabled in schema)
  if (schema.loadStatus && appliedFilters.loadStatus && appliedFilters.loadStatus.length > 0) {
    params.loadStatus = appliedFilters.loadStatus.join(',');
  }

  if (schema.paymentType && appliedFilters.paymentType) {
    params.paymentType = appliedFilters.paymentType;
  }

  if (schema.paymentStatus && appliedFilters.paymentStatus && appliedFilters.paymentStatus.length > 0) {
    params.paymentStatus = appliedFilters.paymentStatus.join(',');
  }

  if (schema.search && appliedFilters.search) {
    params.search = appliedFilters.search.trim();
  }

  if (schema.userRole && appliedFilters.userRole && appliedFilters.userRole.length > 0) {
    params.userRole = appliedFilters.userRole.join(',');
  }

  if (schema.userStatus && appliedFilters.userStatus && appliedFilters.userStatus.length > 0) {
    params.userStatus = appliedFilters.userStatus.join(',');
  }

  if (schema.carrierStatus && appliedFilters.carrierStatus && appliedFilters.carrierStatus.length > 0) {
    params.carrierStatus = appliedFilters.carrierStatus.join(',');
  }

  if (schema.customerStatus && appliedFilters.customerStatus && appliedFilters.customerStatus.length > 0) {
    params.customerStatus = appliedFilters.customerStatus.join(',');
  }

  if (schema.customerType && appliedFilters.customerType) {
    params.customerType = appliedFilters.customerType;
  }

  if (schema.shipmentType && appliedFilters.shipmentType) {
    params.shipmentType = appliedFilters.shipmentType;
  }

  return params;
}

/**
 * Build query params for GET /api/stats (StatsSnapshot API)
 * @param {Object} appliedFilters - Applied filter values (period, startDate, endDate, scope, scopeId, loadStatus)
 * @param {string} activeTab - Current active tab
 * @returns {Object} { period, from, to, scopeType, scopeId?, statuses? }
 */
export function buildStatsSnapshotParams(appliedFilters, activeTab) {
  const { startDate, endDate } = getDateRangeForPeriod(
    appliedFilters?.period || defaultFilters.period,
    appliedFilters?.startDate,
    appliedFilters?.endDate
  );
  const fromDate = startDate ? new Date(startDate) : null;
  const toDate = endDate ? new Date(endDate) : null;
  const from = fromDate ? fromDate.toISOString().slice(0, 10) : undefined;
  const to = toDate ? toDate.toISOString().slice(0, 10) : undefined;

  const params = {
    period: 'range',
    from,
    to
  };

  const scope = appliedFilters?.scope ?? defaultFilters.scope;
  const scopeType = scope === 'global' ? 'global' : (scope === 'user' ? 'user' : scope === 'customer' ? 'customer' : scope === 'carrier' ? 'carrier' : 'global');
  params.scopeType = scopeType;
  if (scopeType !== 'global' && appliedFilters?.scopeId) {
    params.scopeId = appliedFilters.scopeId;
  }

  const schema = filtersSchemaByTab[activeTab] || filtersSchemaByTab.overview;
  if (schema.loadStatus && appliedFilters?.loadStatus && appliedFilters.loadStatus.length > 0) {
    const normalized = appliedFilters.loadStatus.map((s) => {
      const lower = String(s).toLowerCase().replace(/\s+/g, '');
      if (lower === 'pickedup') return 'pickedUp';
      if (lower === 'onhold') return 'onHold';
      if (lower === 'cancelled' || lower === 'canceled') return 'cancelled';
      return lower;
    });
    params.statuses = normalized.join(',');
  }

  return params;
}

/**
 * Check if two filter objects are equal (for deduplication)
 * @param {Object} filters1
 * @param {Object} filters2
 * @returns {boolean}
 */
export function areFiltersEqual(filters1, filters2) {
  const keys1 = Object.keys(filters1).sort();
  const keys2 = Object.keys(filters2).sort();
  
  if (keys1.length !== keys2.length) return false;
  
  for (const key of keys1) {
    if (Array.isArray(filters1[key]) && Array.isArray(filters2[key])) {
      if (JSON.stringify(filters1[key].sort()) !== JSON.stringify(filters2[key].sort())) {
        return false;
      }
    } else if (filters1[key] !== filters2[key]) {
      return false;
    }
  }
  
  return true;
}
